import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import EditIcon from '@mui/icons-material/Edit';
import { useEvents } from '../../contexts/EventContext';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import { ConfirmDialog } from '../../components/common';
import QRCodeDisplay from '../../components/QRCodeDisplay';
import { formatDate, formatTime, isDateInPast, isDateTodayOrFuture } from '../../utils/helpers';
import { getCategoryColor, getCategoryLabel, RegistrationAttendee } from '../../types';
import { RELATIONSHIP_LABELS, Relationship, FamilyMember } from '../../types/user';
import { attendeeName, attendeeRelationshipLabel, isGuestAttendee, visibleAttendees } from '../../utils/attendee';

const MyRegistrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { events, getUserRegistrations, cancelRegistration, cancelAttendee, modifyRegistration } = useEvents();
  const { user } = useAuth();
  const { features } = useConfig();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Registration cancelled successfully');
  const [isProcessing, setIsProcessing] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrRegistration, setQrRegistration] = useState<{
    id: string;
    eventTitle: string;
    attendees?: RegistrationAttendee[];
  } | null>(null);

  // Attendee cancel state
  const [cancelAttendeeDialogOpen, setCancelAttendeeDialogOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState<{
    id: string;
    name: string;
    isLastAttendee: boolean;
  } | null>(null);

  // Edit registration state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRegistration, setEditRegistration] = useState<{
    id: string;
    eventTitle: string;
    eventId: string;
    currentAttendeeIds: string[];
    checkedInAttendeeIds: string[];
  } | null>(null);
  const [selectedFamilyMemberIds, setSelectedFamilyMemberIds] = useState<string[]>([]);
  const [editError, setEditError] = useState<string | null>(null);

  const userRegistrations = user ? getUserRegistrations(user.id) : [];

  // Enrich registrations with event data
  const enrichedRegistrations = userRegistrations.map((reg) => {
    const event = events.find((e) => e.id === reg.eventId);
    return { ...reg, event };
  });

  // Separate into upcoming and past/cancelled
  const activeRegistrations = enrichedRegistrations.filter(
    (r) => r.status === 'confirmed' && r.event && isDateTodayOrFuture(r.event.date)
  );

  const pastRegistrations = enrichedRegistrations.filter(
    (r) =>
      r.status === 'cancelled' ||
      (r.event && isDateInPast(r.event.date))
  );

  const handleCancelClick = (registrationId: string) => {
    setSelectedRegistrationId(registrationId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedRegistrationId) return;

    setIsProcessing(true);
    try {
      await cancelRegistration(selectedRegistrationId);
      setSuccessMessage('Registration cancelled successfully');
      setShowSuccess(true);
    } catch (err) {
      console.error('Failed to cancel registration:', err);
    } finally {
      setIsProcessing(false);
      setCancelDialogOpen(false);
      setSelectedRegistrationId(null);
    }
  };

  const handleCancelCancel = () => {
    setCancelDialogOpen(false);
    setSelectedRegistrationId(null);
  };

  // Attendee cancellation handlers
  const handleCancelAttendeeClick = (attendee: RegistrationAttendee, totalAttendees: number) => {
    setSelectedAttendee({
      id: attendee.id,
      name: attendeeName(attendee),
      isLastAttendee: totalAttendees === 1,
    });
    setCancelAttendeeDialogOpen(true);
  };

  const handleCancelAttendeeConfirm = async () => {
    if (!selectedAttendee) return;

    setIsProcessing(true);
    try {
      const result = await cancelAttendee(selectedAttendee.id);
      if (result.type === 'registration_cancelled') {
        setSuccessMessage('Registration cancelled (last attendee removed)');
      } else {
        setSuccessMessage(`${selectedAttendee.name} removed from registration`);
      }
      setShowSuccess(true);
    } catch (err) {
      console.error('Failed to cancel attendee:', err);
    } finally {
      setIsProcessing(false);
      setCancelAttendeeDialogOpen(false);
      setSelectedAttendee(null);
    }
  };

  const handleCancelAttendeeCancel = () => {
    setCancelAttendeeDialogOpen(false);
    setSelectedAttendee(null);
  };

  // Helper to check if registration can be edited (before deadline)
  const canEditRegistration = (event: { registrationDeadline: string; registrationDeadlineTime?: string }) => {
    const deadlineDate = new Date(event.registrationDeadline);
    const year = deadlineDate.getUTCFullYear();
    const month = deadlineDate.getUTCMonth();
    const day = deadlineDate.getUTCDate();

    let deadlineDateTime: Date;
    if (event.registrationDeadlineTime) {
      const [hours, minutes] = event.registrationDeadlineTime.split(':').map(Number);
      deadlineDateTime = new Date(Date.UTC(year, month, day, hours, minutes, 59, 999));
    } else {
      deadlineDateTime = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
    }

    return deadlineDateTime > new Date();
  };

  // Edit registration handlers
  const handleEditClick = (registration: typeof enrichedRegistrations[0]) => {
    if (!registration.event || !registration.attendees) return;

    // Plan 4.10 — the edit dialog only manages family-member attendees;
    // guest add/remove flows through the inline picker on EventDetailPage
    // (Plan 4.11). Filter out guest rows here so the existing edit logic
    // stays correct.
    const currentAttendeeIds = registration.attendees
      .filter((a) => a.familyMember)
      .map((a) => a.familyMember!.id);
    const checkedInAttendeeIds = registration.attendees
      .filter((a) => a.checkedIn && a.familyMember)
      .map((a) => a.familyMember!.id);

    setEditRegistration({
      id: registration.id,
      eventTitle: registration.event.title,
      eventId: registration.eventId,
      currentAttendeeIds,
      checkedInAttendeeIds,
    });
    setSelectedFamilyMemberIds(currentAttendeeIds);
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editRegistration) return;

    // Validate at least one attendee selected
    if (selectedFamilyMemberIds.length === 0) {
      setEditError('Please select at least one family member');
      return;
    }

    // Ensure checked-in attendees are still selected
    const missingCheckedIn = editRegistration.checkedInAttendeeIds.filter(
      (id) => !selectedFamilyMemberIds.includes(id)
    );
    if (missingCheckedIn.length > 0) {
      setEditError('Cannot remove already checked-in attendees');
      return;
    }

    setIsProcessing(true);
    setEditError(null);

    try {
      await modifyRegistration(editRegistration.id, {
        familyMemberIds: selectedFamilyMemberIds,
      });
      setSuccessMessage('Registration updated successfully');
      setShowSuccess(true);
      setEditDialogOpen(false);
      setEditRegistration(null);
    } catch (err: any) {
      setEditError(err.message || 'Failed to update registration');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditRegistration(null);
    setEditError(null);
  };

  const handleFamilyMemberToggle = (memberId: string) => {
    setSelectedFamilyMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Get family members for edit dialog — de-duplicated, one row per living
  // person, with angels excluded (never offered as an event attendee).
  const familyMembers: FamilyMember[] = visibleAttendees(user?.familyMembers || []);

  const renderRegistrationCard = (registration: typeof enrichedRegistrations[0], showCancel: boolean) => {
    if (!registration.event) return null;

    const event = registration.event;
    const isPast = isDateInPast(event.date);
    const isCancelled = registration.status === 'cancelled';

    return (
      <Card
        key={registration.id}
        sx={{
          opacity: isPast || isCancelled ? 0.7 : 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            height: 6,
            backgroundColor: getCategoryColor(event.category),
          }}
        />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Chip
              label={getCategoryLabel(event.category)}
              size="small"
              sx={{
                backgroundColor: getCategoryColor(event.category),
                color: 'white',
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {registration.checkedIn && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Checked In"
                  size="small"
                  color="success"
                />
              )}
              {isCancelled && (
                <Chip label="Cancelled" size="small" color="error" />
              )}
              {isPast && !isCancelled && !registration.checkedIn && (
                <Chip label="Past Event" size="small" color="default" />
              )}
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom>
            {event.title}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDate(event.date)} at {formatTime(event.startTime)}
              </Typography>
            </Box>
            {event.locationIsOnline || registration.eventIsOnline ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Online (Zoom)
                </Typography>
                {registration.meetingUrl?.startsWith('https://') && (
                  <Button
                    variant="contained"
                    size="small"
                    component="a"
                    href={registration.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon />}
                  >
                    Join on Zoom
                  </Button>
                )}
              </Box>
            ) : (
            <Box
              component="a"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { color: '#1976d2' },
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <LocationOnIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {event.location}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: 14, color: 'action.active' }} />
            </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Attendees:
              </Typography>
            </Box>

            {/* Individual attendee list with cancel buttons */}
            {registration.attendees && registration.attendees.length > 0 ? (
              <List dense sx={{ py: 0, pl: 3 }}>
                {registration.attendees.map((attendee) => (
                  <ListItem key={attendee.id} sx={{ py: 0.5, px: 0 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {attendeeName(attendee)}
                          </Typography>
                          <Chip
                            label={
                              isGuestAttendee(attendee)
                                ? `${attendeeRelationshipLabel(attendee)} (Guest)`
                                : RELATIONSHIP_LABELS[attendee.familyMember!.relationship as Relationship] ||
                                  attendee.familyMember!.relationship
                            }
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          {attendee.checkedIn && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label="Checked In"
                              size="small"
                              color="success"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      }
                    />
                    {showCancel && !isCancelled && !isPast && !attendee.checkedIn && (
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove from registration">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleCancelAttendeeClick(attendee, registration.attendees!.length)}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>
                {registration.attendeeNames && registration.attendeeNames.length > 0
                  ? registration.attendeeNames.join(', ')
                  : `${registration.attendeeCount} attendee${registration.attendeeCount !== 1 ? 's' : ''}`}
              </Typography>
            )}
          </Box>

          {registration.specialRequirements && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Notes: {registration.specialRequirements}
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/events/${event.id}`, { state: { from: 'my-registrations' } })}
          >
            View Event
          </Button>
          {features.qrCheckIn && showCancel && !isCancelled && !isPast && registration.status === 'confirmed' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<QrCodeIcon />}
              onClick={() => {
                // Debug: Log registration data when QR button clicked
                console.log('[MyRegistrations] QR button clicked:', {
                  registrationId: registration.id,
                  attendeeCount: registration.attendeeCount,
                  attendeesArrayLength: registration.attendees?.length ?? 0,
                  attendees: registration.attendees?.map(a => ({
                    id: a.id,
                    name: a.familyMember?.name,
                    checkedIn: a.checkedIn
                  }))
                });
                setQrRegistration({
                  id: registration.id,
                  eventTitle: event.title,
                  attendees: registration.attendees,
                });
                setQrDialogOpen(true);
              }}
            >
              QR Codes
            </Button>
          )}
          {showCancel && !isCancelled && !isPast && canEditRegistration(event) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditClick(registration)}
            >
              Edit Attendees
            </Button>
          )}
          {showCancel && !isCancelled && !isPast && !registration.checkedIn && (
            <Button
              color="error"
              size="small"
              onClick={() => handleCancelClick(registration.id)}
            >
              Cancel All
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        My Registrations
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and manage your event registrations
      </Typography>

      {/* Active Registrations */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Upcoming Events
      </Typography>

      {activeRegistrations.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          You haven't registered for any upcoming events.{' '}
          <Button
            size="small"
            onClick={() => navigate('/events')}
            sx={{ ml: 1 }}
          >
            Browse Events
          </Button>
        </Alert>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {activeRegistrations.map((reg) => (
            <Grid item xs={12} sm={6} lg={4} key={reg.id}>
              {renderRegistrationCard(reg, true)}
            </Grid>
          ))}
        </Grid>
      )}

      {/* Past/Cancelled Registrations */}
      {pastRegistrations.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Past & Cancelled
          </Typography>
          <Grid container spacing={3}>
            {pastRegistrations.map((reg) => (
              <Grid item xs={12} sm={6} lg={4} key={reg.id}>
                {renderRegistrationCard(reg, false)}
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Confirm Cancel Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel Registration"
        message="Are you sure you want to cancel this registration? This action cannot be undone."
        confirmText={isProcessing ? 'Cancelling...' : 'Cancel Registration'}
        cancelText="Keep Registration"
        confirmColor="error"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelCancel}
      />

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Event Check-in QR Code
          <IconButton onClick={() => setQrDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {qrRegistration && (
            <QRCodeDisplay
              registrationId={qrRegistration.id}
              attendees={qrRegistration.attendees}
              eventTitle={qrRegistration.eventTitle}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Attendee Dialog */}
      <ConfirmDialog
        open={cancelAttendeeDialogOpen}
        title="Remove Attendee"
        message={
          selectedAttendee?.isLastAttendee
            ? `Are you sure you want to remove ${selectedAttendee?.name}? This is the last attendee, so the entire registration will be cancelled.`
            : `Are you sure you want to remove ${selectedAttendee?.name} from this registration?`
        }
        confirmText={isProcessing ? 'Removing...' : 'Remove'}
        cancelText="Keep"
        confirmColor="error"
        onConfirm={handleCancelAttendeeConfirm}
        onCancel={handleCancelAttendeeCancel}
      />

      {/* Edit Attendees Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Attendees
          <IconButton onClick={handleEditCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editRegistration && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select who will attend <strong>{editRegistration.eventTitle}</strong>
              </Typography>

              {editError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {editError}
                </Alert>
              )}

              <FormGroup>
                {familyMembers.map((member) => {
                  const isCheckedIn = editRegistration.checkedInAttendeeIds.includes(member.id);
                  return (
                    <FormControlLabel
                      key={member.id}
                      control={
                        <Checkbox
                          checked={selectedFamilyMemberIds.includes(member.id)}
                          onChange={() => handleFamilyMemberToggle(member.id)}
                          disabled={isCheckedIn || isProcessing}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{member.name}</Typography>
                          <Chip
                            label={RELATIONSHIP_LABELS[member.relationship] || member.relationship}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          {isCheckedIn && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label="Checked In"
                              size="small"
                              color="success"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </FormGroup>

              {familyMembers.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No family members found. Please contact Team Jak to add family members.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={isProcessing || selectedFamilyMemberIds.length === 0}
          >
            {isProcessing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyRegistrationsPage;
